/**
 * Data validation utilities
 */

import PropTypes from 'prop-types';

// Common PropTypes validators
export const propTypes = {
  // Image URL validator
  imageUrl: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.shape({
      url: PropTypes.string,
      alt: PropTypes.string,
      width: PropTypes.number,
      height: PropTypes.number,
    })
  ]),
  
  // API response validator
  apiResponse: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    created_at: PropTypes.string,
    updated_at: PropTypes.string,
    is_active: PropTypes.bool,
  }),
  
  // Paginated response
  paginatedResponse: PropTypes.shape({
    count: PropTypes.number.isRequired,
    next: PropTypes.string,
    previous: PropTypes.string,
    results: PropTypes.array.isRequired,
  }),
  
  // SEO data
  seoData: PropTypes.shape({
    meta_title: PropTypes.string,
    meta_description: PropTypes.string,
    meta_keywords: PropTypes.string,
    og_title: PropTypes.string,
    og_description: PropTypes.string,
    og_image: PropTypes.string,
    twitter_title: PropTypes.string,
    twitter_description: PropTypes.string,
    twitter_image: PropTypes.string,
    canonical_url: PropTypes.string,
    robots_txt: PropTypes.string,
  }),
  
  // User input validation
  email: function(props, propName, componentName) {
    const email = props[propName];
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      return new Error(
        `Invalid prop '${propName}' supplied to '${componentName}'. Expected a valid email address.`
      );
    }
  },
  
  phone: function(props, propName, componentName) {
    const phone = props[propName];
    if (phone && !/^[\d\s\-\+\(\)]{10,20}$/.test(phone)) {
      return new Error(
        `Invalid prop '${propName}' supplied to '${componentName}'. Expected a valid phone number.`
      );
    }
  },
  
  url: function(props, propName, componentName) {
    const url = props[propName];
    if (url && !/^https?:\/\/.+\..+/.test(url)) {
      return new Error(
        `Invalid prop '${propName}' supplied to '${componentName}'. Expected a valid URL.`
      );
    }
  },
};

// Validation functions
export const validateEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

export const validatePhone = (phone) => {
  return /^[\d\s\-\+\(\)]{10,20}$/.test(phone);
};

export const validateUrl = (url) => {
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

export const validateRequired = (value) => {
  return value !== undefined && value !== null && value !== '';
};

export const validateMinLength = (value, min) => {
  return value && value.length >= min;
};

export const validateMaxLength = (value, max) => {
  return value && value.length <= max;
};

export const validatePattern = (value, pattern) => {
  return pattern.test(value);
};

// Sanitization functions for XSS protection
export const sanitizeHTML = (html) => {
  if (typeof window === 'undefined') return html;
  
  // In production, use DOMPurify library
  if (process.env.NODE_ENV === 'production') {
    try {
      const DOMPurify = require('dompurify');
      return DOMPurify.sanitize(html, {
        ALLOWED_TAGS: ['p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'strong', 'em', 'u', 's', 
                      'blockquote', 'code', 'pre', 'br', 'hr', 'ul', 'ol', 'li', 'a', 
                      'img', 'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div', 'span'],
        ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'style', 'target', 'rel'],
        ALLOW_DATA_ATTR: false
      });
    } catch (error) {
      console.warn('DOMPurify not available, using basic sanitization');
    }
  }
  
  // Basic sanitization for development
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = html;
  
  // Remove script tags
  const scripts = tempDiv.querySelectorAll('script');
  scripts.forEach(script => script.remove());
  
  // Remove event handlers
  const elements = tempDiv.querySelectorAll('*');
  elements.forEach(el => {
    const attributes = el.attributes;
    for (let i = attributes.length - 1; i >= 0; i--) {
      const attr = attributes[i];
      if (attr.name.startsWith('on')) {
        el.removeAttribute(attr.name);
      }
    }
  });
  
  return tempDiv.innerHTML;
};

export const sanitizeText = (text) => {
  if (!text) return '';
  return text
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

// Form validation helper
export const validateForm = (formData, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const value = formData[field];
    const fieldRules = rules[field];
    
    if (fieldRules.required && !validateRequired(value)) {
      errors[field] = fieldRules.requiredMessage || 'This field is required';
      return;
    }
    
    if (value) {
      if (fieldRules.minLength && !validateMinLength(value, fieldRules.minLength)) {
        errors[field] = fieldRules.minLengthMessage || `Minimum ${fieldRules.minLength} characters required`;
      } else if (fieldRules.maxLength && !validateMaxLength(value, fieldRules.maxLength)) {
        errors[field] = fieldRules.maxLengthMessage || `Maximum ${fieldRules.maxLength} characters allowed`;
      } else if (fieldRules.pattern && !validatePattern(value, fieldRules.pattern)) {
        errors[field] = fieldRules.patternMessage || 'Invalid format';
      } else if (fieldRules.validate) {
        const customError = fieldRules.validate(value, formData);
        if (customError) {
          errors[field] = customError;
        }
      }
    }
  });
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

export default {
  propTypes,
  validateEmail,
  validatePhone,
  validateUrl,
  validateRequired,
  validateMinLength,
  validateMaxLength,
  validatePattern,
  sanitizeHTML,
  sanitizeText,
  validateForm,
};